// ABOUTME: Period-over-period pivot mode — compares the first half of the visible period against the second half per row.
// ABOUTME: Shows avg → avg, Δ% and a direction bar, with clickable column sorting. All derived from the row values, no backend data needed.
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../theme";
import Text from "../../../../theme/text/Text";
import { RhPivotTableRow } from "./RhPivotTableRow";
import { pivotRowCursor } from "../../reportUtils";

const avg = (arr) => (arr.length ? arr.reduce((sum, v) => sum + (Number(v) || 0), 0) / arr.length : 0);
const sum = (arr) => arr.reduce((s, v) => s + (Number(v) || 0), 0);
const round1 = (n) => Math.round(n * 10) / 10;

const AGG_OPTIONS = [
    { value: 'average', key: 'average' },
    { value: 'total', key: 'total' },
];

// Average vs total toggle, shared across the pivot mode tables; matches the heatmap toggle look.
export const AggToggle = ({ value, onChange }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDark = theme.palette.mode === "dark";
    const accent = isDark ? colors.blue : colors.primary;

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
            <ToggleButtonGroup
                exclusive
                value={value}
                onChange={(e, v) => v && onChange(v)}
                sx={{
                    padding: 0.5,
                    borderRadius: 5,
                    background: isDark ? colors.grey[300] : colors.grey[100],
                    height: 40,
                    gap: 0.5,
                }}
            >
                {AGG_OPTIONS.map(opt => (
                    <ToggleButton
                        key={opt.value}
                        value={opt.value}
                        sx={{
                            border: 'none',
                            borderRadius:"16px !important",
                            px: 1.5,
                            height: 32,

                            textTransform: 'none',
                            fontSize: 14,
                            fontWeight: 500,
                            color: colors.txt['secondary'],
                            '&.Mui-selected': {
                                background: accent[100],
                                color: accent[400],
                                fontWeight: 700,
                                '&:hover': { background: accent[100] },
                            },
                        }}
                    >
                        {t(opt.key)}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
};

// Direction bar: a centred track with a bar growing right (green, up) or left (red, down), capped at ±100%.
export const DirectionBar = ({ delta }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const TRACK = 160;
    const half = TRACK / 2;
    const magnitude = delta === null ? 0 : Math.min(Math.abs(delta), 100) / 100 * half;
    const isUp = (delta || 0) > 0;
    const barColor = delta === null || Math.abs(delta) < 1
        ? colors.grey[400]
        : isUp ? colors.green[400] : colors.red[400];

    return (
        <Box sx={{ position: 'relative', width: TRACK, height: 12, background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100], borderRadius: 1 }}>
            <Box sx={{ position: 'absolute', left: half, top: -2, bottom: -2, width: '1px', background: colors.grey[400] }} />
            <Box
                sx={{
                    position: 'absolute',
                    top: 2,
                    bottom: 2,
                    borderRadius: 1,
                    background: barColor,
                    ...(isUp
                        ? { left: half, width: magnitude }
                        : { left: half - magnitude, width: magnitude }),
                }}
            />
        </Box>
    );
};

export const DeltaPill = ({ delta }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const hasDelta = delta !== null && isFinite(delta);
    const isFlat = hasDelta && Math.abs(delta) < 1;
    const isUp = hasDelta && delta > 0;
    const arrow = !hasDelta ? '–' : (isFlat ? '→' : (isUp ? '↑' : '↓'));
    const palette = (!hasDelta || isFlat)
        ? { bg: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100], text: colors.grey[400] }
        : isUp ? { bg: colors.green[100], text: colors.green[400] } : { bg: colors.red[100], text: colors.red[400] };

    return (
        <Box 
            sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                background: palette.bg, 
                color: palette.text, 
                borderRadius: 1, 
                px: 2,
                gap:1, 
                height: 32, 
                minWidth: 100, 
                fontWeight: 700, 
                fontSize: 14,
                fontFamily:"Roboto,sans-serif"
            }}
        >
            {hasDelta ? 
            <>
                <span>
                    {arrow}
                </span> 
                
                <span>
                    {Math.abs(delta).toFixed(1)}%
                </span>
            </>
            : '–'
            }
        </Box>
    );
};

export const RhPivotPeriodMode = ({ data, rows, chartOption, currentReport, setCurrentReport, onRowClick }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const columns = data?.columns || [];
    const half = Math.floor(columns.length / 2);

    const firstLabel = columns.length ? `${columns[0]} – ${columns[Math.max(0, half - 1)]}` : '';
    const secondLabel = columns.length ? `${columns[half]} – ${columns[columns.length - 1]}` : '';

    // Aggregation + sort live on the report so a saved/reopened report keeps the chosen view.
    const agg = currentReport?.pivot?.periodAgg ?? 'average';
    const setAgg = (value) => setCurrentReport(prev => ({
        ...prev,
        pivot: { ...prev?.pivot, periodAgg: value },
    }));
    const sortState = currentReport?.pivot?.periodSort ?? { key: 'delta', dir: 'desc' };

    const onSort = (key) => {
        const next = sortState.key === key
            ? { key, dir: sortState.dir === 'desc' ? 'asc' : 'desc' }
            : { key, dir: 'desc' };
        setCurrentReport(prev => ({
            ...prev,
            pivot: { ...prev?.pivot, periodSort: next },
        }));
    };

    const movers = useMemo(() => {
        const aggFn = agg === 'total' ? sum : avg;
        const mapped = (rows || []).map((row, index) => {
            const values = row.values || [];
            const firstAvg = round1(aggFn(values.slice(0, half)));
            const secondAvg = round1(aggFn(values.slice(half)));
            const base = aggFn(values.slice(0, half));
            const delta = base > 0 ? round1(((aggFn(values.slice(half)) - base) / base) * 100) : null;
            return { row, index, firstAvg, secondAvg, delta };
        });

        const mult = sortState.dir === 'asc' ? 1 : -1;
        mapped.sort((a, b) => {
            const av = a[sortState.key];
            const bv = b[sortState.key];
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            return (av - bv) * mult;
        });
        return mapped;
    }, [rows, half, sortState.key, sortState.dir, agg]);

    const totals = useMemo(() => {
        const aggFn = agg === 'total' ? sum : avg;
        const colTotals = data?.columnTotals || [];
        const firstVals = colTotals.slice(0, half);
        const secondVals = colTotals.slice(half);
        const base = aggFn(firstVals);
        const delta = base > 0 ? round1(((aggFn(secondVals) - base) / base) * 100) : null;
        return { firstAvg: round1(base), secondAvg: round1(aggFn(secondVals)), delta };
    }, [data, half, agg]);

    const cellSx = { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' };
    const rowBorder = `1px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`;
    const sortArrow = (key) => (sortState.key === key ? (sortState.dir === 'desc' ? ' ▼' : ' ▲') : '');

    const SortHeader = ({ sortKey, label }) => (
        <Box sx={{ ...cellSx, cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(sortKey)}>
            <Text bold={sortState.key === sortKey}>{label}{sortArrow(sortKey)}</Text>
        </Box>
    );

    return (
        <Box sx={{ width: 1, pb: 6 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', height: 56, borderBottom: `2px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`, position: 'sticky', top: 72, background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0], zIndex: 1 }}>
               <Box 
                    sx={{ 
                        width: 320, 
                        pr: 1.5,
                        display:'flex',
                        justifyContent:'space-between',
                        gap:1,
                    }}
                >
                    <RhPivotTableRow chartOption={chartOption} currentReport={currentReport} setCurrentReport={setCurrentReport} />
                    <AggToggle value={agg} onChange={setAgg} />
                </Box>
                <SortHeader sortKey="firstAvg" label={`${firstLabel}`} />
                <Box sx={{ width: 40, ...cellSx, flex: 'unset' }}><Text>→</Text></Box>
                <SortHeader sortKey="secondAvg" label={`${secondLabel}`} />
                <SortHeader sortKey="delta" label="Δ%" />
                <Box sx={cellSx}><Text bold>{t('direction')}</Text></Box>
            </Box>

            {/* Totals */}
            <Box sx={{ display: 'flex', alignItems: 'center', height: 48, borderBottom: `2px solid ${theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}`, background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100], position: 'sticky', top: 128, zIndex: 1 }}>
                <Box sx={{ width: 320, pl: 1 }}><Text bold>{t('total')}</Text></Box>
                <Box sx={cellSx}><Text bold>{totals.firstAvg}</Text></Box>
                <Box sx={{ width: 40, ...cellSx, flex: 'unset' }} />
                <Box sx={cellSx}><Text bold>{totals.secondAvg}</Text></Box>
                <Box sx={cellSx}><DeltaPill delta={totals.delta} /></Box>
                <Box sx={cellSx}><DirectionBar delta={totals.delta} /></Box>
            </Box>

            {/* Rows */}
            {movers.map(({ row, index, firstAvg, secondAvg, delta }) => (
                <Box
                    key={index}
                    onClick={() => onRowClick?.(index)}
                    sx={{ display: 'flex', alignItems: 'center', height: 48, borderBottom: rowBorder, cursor: pivotRowCursor(row), "&:hover": { background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100] } }}
                >
                    <Box sx={{ width: 320, pl: 1, overflow: 'hidden' }}>
                        <Text sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{t(row.name) || t(row.description)}</Text>
                    </Box>
                    <Box sx={cellSx}><Text>{firstAvg}</Text></Box>
                    <Box sx={{ width: 40, ...cellSx, flex: 'unset' }}><Text light>→</Text></Box>
                    <Box sx={cellSx}><Text>{secondAvg}</Text></Box>
                    <Box sx={cellSx}><DeltaPill delta={delta} /></Box>
                    <Box sx={cellSx}><DirectionBar delta={delta} /></Box>
                </Box>
            ))}
        </Box>
    );
};
